import React, { useEffect, useRef, useState } from "react";
import { Bell, BellRing, CheckCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import api from "../lib/api";

const pushSupported = typeof window !== "undefined" && "Notification" in window && "serviceWorker" in navigator;

export const NotificationBell = () => {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const [perm, setPerm] = useState(pushSupported ? Notification.permission : "unsupported");
  const seen = useRef(null);
  const navigate = useNavigate();

  const poll = async () => {
    if (!navigator.onLine) return;
    const { data: d } = await api.get("/notifications", { params: { tz_offset: new Date().getTimezoneOffset() } });
    setData(d);
    const fresh = seen.current ? d.items.filter((n) => !n.read && !seen.current.has(n.id)) : [];
    seen.current = new Set(d.items.map((n) => n.id));
    if (fresh.length && pushSupported && Notification.permission === "granted") {
      const reg = await navigator.serviceWorker.ready;
      fresh.forEach((n) => reg.showNotification(n.title, { body: n.body, icon: "/icons/icon-192.png", badge: "/icons/icon-192.png", tag: n.id, data: { url: n.link } }));
    }
  };

  useEffect(() => {
    poll().catch(() => {});
    const t = setInterval(() => poll().catch(() => {}), 60000);
    return () => clearInterval(t);
  }, []);

  const enablePush = async () => setPerm(await Notification.requestPermission());

  const openItem = async (n) => {
    if (!n.read) await api.post(`/notifications/${n.id}/read`);
    setOpen(false);
    navigate(n.link || "/scheduling");
    poll().catch(() => {});
  };

  const readAll = async () => { await api.post("/notifications/read-all"); poll().catch(() => {}); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="relative p-2.5 rounded-full" style={{ background: "var(--mf-surface)" }} aria-label="Notifications" data-testid="notification-bell">
          {data.unread ? <BellRing className="w-5 h-5" style={{ color: "var(--mf-primary)" }} /> : <Bell className="w-5 h-5" style={{ color: "var(--mf-text-muted)" }} />}
          {data.unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold text-white flex items-center justify-center" style={{ background: "var(--mf-accent)" }} data-testid="notification-count">{data.unread}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[min(22rem,calc(100vw-2rem))] p-0 rounded-2xl overflow-hidden" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <span className="font-serif text-lg">Reminders</span>
          {data.unread > 0 && (
            <button onClick={readAll} className="text-xs flex items-center gap-1" style={{ color: "var(--mf-primary)" }} data-testid="notification-read-all"><CheckCheck className="w-3.5 h-3.5" /> Mark all read</button>
          )}
        </div>
        {pushSupported && perm === "default" && (
          <button onClick={enablePush} className="w-full text-left px-4 py-3 text-sm flex items-center gap-2" style={{ background: "rgba(201,168,76,0.12)" }} data-testid="enable-push-btn">
            <BellRing className="w-4 h-4" style={{ color: "var(--mf-accent)" }} /> Turn on device notifications for session reminders
          </button>
        )}
        <div className="max-h-80 overflow-y-auto">
          {data.items.length === 0 ? (
            <div className="px-4 py-8 text-sm text-center" style={{ color: "var(--mf-text-muted)" }} data-testid="notification-empty">No reminders yet. You'll be notified 24h, 1h and 15 min before each session.</div>
          ) : data.items.map((n) => (
            <button key={n.id} onClick={() => openItem(n)} className="w-full text-left px-4 py-3 border-b last:border-0 hover:bg-black/5 flex gap-3" data-testid={`notification-${n.id}`}>
              <span className="mt-1.5 w-2 h-2 rounded-full shrink-0" style={{ background: n.read ? "transparent" : "var(--mf-accent)" }} />
              <span className="min-w-0">
                <span className="block text-sm font-medium">{n.title}</span>
                <span className="block text-xs truncate" style={{ color: "var(--mf-text-muted)" }}>{n.body}</span>
              </span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
